import React from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, History } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";

/**
 * Iter 775 — `/engage-test/customer` alias → the customer wizard.
 *
 * The shared backend's receipt and 24h/4h reminder e-mails carry a
 * "Finish my review" button whose `resume_link` opens this path with the
 * session reference. We resolve it to `/engage/{qr}?resume={session_id}`
 * — and on the wizard side `?resume=` wins over the store QR, so the
 * exact session reopens at the review step (no re-scan, no loop).
 *
 * Accepted query params (defensive — link formats may vary):
 *   session id: resume | session_id | session | sid
 *   store QR:   qr | qr_code_id | code | store
 * When only the session id is present, GET /engagement/sessions/{id}
 * supplies vendor.qr_code_id (sign-in required — we round-trip /login).
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

function pick(params, names) {
  for (const n of names) {
    const v = params.get(n);
    if (v) return v;
  }
  return null;
}

export default function EngageResumePage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();

  const sessionId = pick(params, ["resume", "session_id", "session", "sid"]);
  const qr = pick(params, ["qr", "qr_code_id", "code", "store"]);

  // Both refs in the link — straight to the wizard.
  if (sessionId && qr) {
    return <Navigate to={`/engage/${encodeURIComponent(qr)}?resume=${encodeURIComponent(sessionId)}`} replace />;
  }
  // No session reference at all — My visits lists every resumable window.
  if (!sessionId) return <Navigate to="/my/visits" replace />;
  // Session-only link needs the API (auth) to resolve the store QR.
  if (!user) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <ResolveSession sessionId={sessionId} nav={nav} />;
}

function ResolveSession({ sessionId, nav }) {
  const q = useQuery({
    queryKey: ["engage-resume-resolve", sessionId],
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/engagement/sessions/${sessionId}`, OPTS);
      // The single-session GET omits vendor.qr_code_id — only
      // sessions/mine carries it. Resolve via the history list.
      let qr = data?.vendor?.qr_code_id || data?.qr_code_id || null;
      if (!qr) {
        for (let offset = 0; offset < 150 && !qr; offset += 50) {
          const { data: mine } = await sharedApi.get("/classifieds/engagement/sessions/mine", {
            ...OPTS,
            params: { ...OPTS.params, limit: 50, offset },
          });
          const rows = mine?.sessions || [];
          qr = rows.find((r) => r.id === sessionId)?.vendor?.qr_code_id || null;
          if (rows.length < 50) break;
        }
      }
      return { session: data, qr };
    },
  });

  const qr = q.data?.qr || null;

  React.useEffect(() => {
    if (qr) nav(`/engage/${encodeURIComponent(qr)}?resume=${encodeURIComponent(sessionId)}`, { replace: true });
  }, [qr, sessionId, nav]);

  if (q.isError || (q.data && !qr)) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="engage-resume-error">
        <Seo title="Resume your visit" />
        <div className="rounded-3xl bg-white shadow-card p-8">
          <History className="w-8 h-8 mx-auto text-slate-300" />
          <h1 className="mt-3 font-display text-xl font-black text-slate-900">
            That visit can&apos;t be reopened
          </h1>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed">
            The session may have closed or its review window has ended. Your visit history has
            every receipt and any review windows still open.
          </p>
          <a
            href="/my/visits"
            data-testid="engage-resume-to-visits"
            className="mt-6 inline-flex items-center justify-center rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-6 py-2.5 text-sm"
          >
            Go to My visits
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] grid place-items-center" data-testid="engage-resume-loading">
      <Seo title="Resume your visit" />
      <div className="text-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        <div className="mt-3 text-sm font-semibold">Opening your visit…</div>
      </div>
    </div>
  );
}
